<template>
  <div >
    <multiselect class="multiselector"
      v-model="selectedOption"
      :options="listOptions"
      :label="listLabel"
      :track-by="listValue"
      placeholder=""
      :clear-on-select="true"
      :show-labels="false"
      :multiple="false"
    />

    <div class="pt-5">
      <div class="text-gray-600 font-bold"> {{ t('forms.church_visit.church_label') }} </div>

      <select id="church-selector" v-model="selectedChurch" @change="setChurch(selectedChurch)">
        <option disabled value="">{{ selectChurchText }}</option>
        <option v-for="church in churches" :value="church" :key="church.id">
          {{ `${church.person_name} - ${generationDisplay(church.generation)} ${church.description ? '- '+church.description : ''} ${church.repeatFlag ? `- ${church.start_date.substring(0, 10)}` : '' }` }}
        </option>
      </select>

      <div v-if="selectedChurch" class="pt-4">
        <div class="text-gray-600 font-bold pb-2">{{ t('forms.common.church_location_label') }}</div>
        <EntityLocationSelector :pointData="pointData" :isRestricted="isRestricted"> </EntityLocationSelector>
      </div>

      <div class="p-3">
        <!-- 9/26/24: disable ability to add churches like this per Jesse's request -->
        <div v-show="false">
          <div class="pb-1" style="font-size: 0.875rem;">{{ missingChurchText }}</div>

          <button @click="openModal()" class="inline-flex items-center px-4 py-2 border text-sm leading-5 font-medium rounded-md focus:outline-none transition duration-150 ease-in-out cursor-pointer border-transparent text-white bg-blue-600 hover:bg-blue-500 focus:ring-blue focus:border-blue-700 active:bg-blue-700" type="button">
            {{ addChurchTitle }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import Multiselect from 'vue-multiselect'
  import axios from 'axios'
  import GlobalEventBus from '@services/GlobalEventBus'
  import ModalService from '@services/ModalService'
  import EntityLocationSelector from '@components/EntityLocationSelector.vue'

  import { onMounted, ref, computed, watch } from 'vue'
  import i18n from '@utils/i18n'

  const { t } = i18n.global

  // Helper function to display generation
  // Church.generation = planter's discipleship.generation (direct mapping)
  const generationDisplay = (generation) => {
    if (!generation) return ''
    const gen = parseInt(generation)

    if (gen === 0) return 'Trainer'
    if (gen === 1) return 'Church Planter'
    if (gen === 2) return 'Titus'
    return `${gen}G`
  }

  const props = defineProps({
    options: String,
    value: Object,
    labelName: String,
    valueName: String,
    hiddenElement: String,
    church_id: Number,
    listenFor: String,
    raiseOnChange: String,
    toClear: String,
    formType: String,
    formResponseEntityLocation: Object,
    church: Object,
    churchEntityLocation: Object,
    extraParams: Object,
  })

  let selectedOption = ref(props.value ? props.value : null)
  let addChurchTitle = ref(gon.add_church_title_text)
  let missingChurchText = ref(gon.missing_church_text)
  let selectChurchText = ref(gon.select_church_text)
  
  let listOptions = ref(JSON.parse(props.options))
  let listLabel = ref(props.labelName ? props.labelName : 'name')
  let listValue = ref(props.valueName ? props.valueName : 'id')

  let render = ref(false)
  let selectedChurch = ref(null)
  let churches = ref([])

  let trainingCenterId = ''
  let churchId = props.church_id

  let pointData = ref(null)

  let isRestricted = ref(false)

  onMounted(() => {
    window.onmessage = (e) =>  {
      let msg = String(e.data)
      if (msg.startsWith('church-created')) {
        let newChurchId = msg.split('|')[1]

        GlobalEventBus.dispatch('church-created', true)

        setTimeout(() => {
          setNewValue(selectedOption.value, newChurchId)
        }, 500)
      }
    }

    // toggle location capabilities based on country restrictions
    GlobalEventBus.subscribe('determine-restriction', restricted => {
      isRestricted.value = restricted
    })

    // First check if the church entity location has data
    if (props.churchEntityLocation && props.churchEntityLocation.latitude && props.churchEntityLocation.longitude) {
      pointData.value = {
        latitude: props.churchEntityLocation.latitude,
        longitude: props.churchEntityLocation.longitude
      }
    }
    // Otherwise, if the form response has location data, use that
    else if (props.formResponseEntityLocation && props.formResponseEntityLocation.latitude && props.formResponseEntityLocation.longitude) {
      pointData.value = {
        latitude: props.formResponseEntityLocation.latitude,
        longitude: props.formResponseEntityLocation.longitude
      }
    }

    GlobalEventBus.subscribe('clear-fields', e => {
      selectedOption.value = null
      selectedChurch.value = null
      churches.value = null
    })

    GlobalEventBus.subscribe(props.toClear, e => {
      selectedOption.value = null
    })

    GlobalEventBus.subscribe('clear-churches', e => {
      selectedOption.value = null
      selectedChurch.value = null
      churches.value = null
    })

    if (props.listenFor) {
      GlobalEventBus.subscribe(props.listenFor, items => {
        getNewOptions(props.listenFor, items)
      })
    }
    
    if (props.value) {
      setNewValue(props.value, churchId)
    }
  })

  watch(selectedOption, (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      setNewValue(newVal)
    }
  })

  function newChurchUrl() {
    let url = `/churches/new?hide_cancel=true&hide_chrome=true&hide_form_buttons=true&training_center_id=${trainingCenterId}`
    console.log('church url', url)
    return url
  }

  function openModal() {
    let data = {
      url: newChurchUrl()
    }

    // debounce this
    ModalService.spawn('forms-church-creation-modal', { data })
  }

  function fetchChurches(newChurchId = null) {
    let params = props.extraParams ? props.extraParams : {}
    params.training_center_id = trainingCenterId
    params.for_church_form = true
    axios.get(`/churches.json`, { params }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      }
    })
    .then(response => {
      let pastChurches = churches.value
      churches.value = response.data

      render.value = true

      let lastChurch = null
      let currentChurch = null
      let index = 0
      let lastIndex = null

      churches.value.forEach(church => {
        currentChurch = `${church.person_name} - ${generationDisplay(church.generation)} ${church.description ? '- '+church.description : ''}`

        if (lastChurch == currentChurch) {
          churches.value[lastIndex].repeatFlag = true
          church.repeatFlag = true
        }

        lastChurch = `${church.person_name} - ${generationDisplay(church.generation)} ${church.description ? '- '+church.description : ''}`

        lastIndex = index
        index = index + 1
      })

      if (newChurchId) {
        setTimeout(() => {
          let church = churches.value.find(ch => ch.id == newChurchId)
          selectedChurch.value = church

          setChurch(selectedChurch.value)
        }, 200)
      }

      return response
    }, error => {
      console.log('error when fetching churches', error)
    })
  }

  function setNewValue(newValue, newChurchId) {
    trainingCenterId = newValue && newValue.training_center_id 

    var el = document.getElementById(props.hiddenElement)
    if (el && newValue){
      console.log('listValue', listValue)
      console.log('newValue', newValue)
      el.value = newValue[listValue]
      el.dispatchEvent(new Event('change'))
      let hiddenTrainingCenterId = document.getElementById('hidden-tc-id')
      hiddenTrainingCenterId.value = newValue.training_center_id

      let hiddenTrainingCenterPersonId = document.getElementById('hidden-p-id')
      hiddenTrainingCenterPersonId.value = newValue.id
    }

    fetchChurches(newChurchId)

    GlobalEventBus.dispatch('paul-change', true)
  }

  function getChurchVisits(value) {
    axios.get(`/field_rep_visits.json`, {
      params: {
        visitable_id: value,
        visitable_type: 'Church',
      }
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      }
    })
    .then(response => {
      let previousVisits = []
      let visits = response.data
      visits.forEach(v => { 
        let vDate = new Date(v.visited_at)
        console.log(v)
        previousVisits.push({visited_on: vDate.toLocaleDateString(), visited_by: v.person_name})

      })
      console.log(response)
      console.log(previousVisits)
      let dupe = document.getElementById('hidden-possible-dupe')
      if (previousVisits.length > 0){
        console.log('spawning Modal')
        ModalService.spawn('duplicate-visit-warning', {title: 'warning', type: 'church-visit', previous_items: previousVisits})
        if (dupe) { dupe.value = true }
      } else {
        if (dupe) { dupe.value = false }
      }
    })

  }

  function setChurch(church) {
    if (church) {
      GlobalEventBus.dispatch('church-change', church)

      // set point data for the entity location selector once we select a church
      pointData.value = {
        latitude: church.latitude,
        longitude: church.longitude
      }

      //a church has been selected, we can hide the add church button
      render.value = false
      let elem = document.getElementById('hidden-c-id')
      elem && (elem.value = church.id)

      let churchSizeElem = document.getElementById('church-size-elem')
      if (churchSizeElem){
        churchSizeElem.value = church.estimated_size
        churchSizeElem.disabled = true
      }

      let churchPlantedDate = document.getElementById('planted-date')
      churchPlantedDate && (churchPlantedDate.value = church.start_date.split('T')[0])

      let hiddenChurchSizeElem = document.getElementById('hidden-church-size-elem')
      hiddenChurchSizeElem && (hiddenChurchSizeElem.value = churchSizeElem.value)

      getChurchVisits(church.id)
    }
  }

  function getNewOptions(type, newValue) {
    let count = 1

    listOptions.value = newValue.data.map(tc => {
      let data = {
        id: tc.relationships.current_paul.data.id,
        tracking_id: count,
        name: tc.attributes.name,
        training_center_id: tc.attributes.id,
        training_center_group_id: tc.attributes.training_center_group_id,
      }

      count++

      return data
    })
  }
</script>

<style lang="scss">
  .multiselector {
    min-width: 14rem;
    max-width: 100%;
  }
</style>
